import { useAuth0 } from '@auth0/auth0-react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { getFlags } from '@chargeafter/feature-flag';
import { secureHttpClient } from '../restClient';
import { addToContext } from './contextManager';
import logger from './loggerManager';
import { environment } from '../environment';

type Scopes = keyof typeof uiAffectingScopes;
type Keys = (typeof uiAffectingScopes)[Scopes];

export type UserProfile = {
  userId: string;
  impersonateEnabled: string;
  peerType: string;
  connectionType: 'UsernamePassword' | 'Sso';
};

export type Nameable = {
  name: string;
  id: string;
};

export interface ChangePassword {
  currentPassword: string;
  newPassword: string;
  confirmedNewPassword: string;
}

const uiAffectingScopes = {
  'salesapp:impersonate': 'SalesAppImpersonate',
  'apply:execute': 'ApplyExecute',
  'checkout:execute': 'CheckoutExecute',
  'store:select': 'StoreSelect',
} as const;

const UserProfileAtom = atom<null | UserProfile>(null);

const UserPermissionsAtom = atom(async () => {
  const userScopes = await secureHttpClient.getGateway(
    {
      fileName: 'userManager.ts',
      fetchingFunction: 'UserPermissionsSelector',
    },
    'v2/allowed_scopes',
  );

  const permissions = (userScopes as Scopes[]).reduce((acc, scope) => {
    return uiAffectingScopes[scope]
      ? { ...acc, [uiAffectingScopes[scope]]: true }
      : acc;
  }, {} as Record<Keys, boolean>);
  return permissions;
});

const ShowErrorAtom = atom(false);

export const useGetUserPermissions = () => useAtomValue(UserPermissionsAtom);
export const useGetUserProfile = () => useAtomValue(UserProfileAtom);

export const useGetShowError = () => useAtomValue(ShowErrorAtom);
export const useSetShowError = () => useSetAtom(ShowErrorAtom);

export const useUserProfile = () => {
  const { getAccessTokenSilently } = useAuth0();
  const updateUserProfile = useSetAtom(UserProfileAtom);
  const setShowError = useSetShowError();
  return async () => {
    const { ffSalesAppShouldUseUserManager } = getFlags();
    const servicePath = ffSalesAppShouldUseUserManager
      ? 'user-manager'
      : 'operate';
    const path = `${environment.backendBaseUrl}/v2/${servicePath}/api/users/profile`;

    try {
      const token = await getAccessTokenSilently();
      logger.log(`Send profile request to ${path}`, {
        filename: 'userManager.ts',
        function: 'useUserProfile',
      });

      const response = await fetch(path, {
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      logger.log(`Received GET response from ${path}`, {
        filename: 'userManager.ts',
        function: 'useUserProfile',
        statusCode: response.status,
        response: data,
      });

      if (data.errors || !response.ok) {
        setShowError(true);
        throw new Error(JSON.stringify(data.errors || data));
      }

      updateUserProfile(data);
      addToContext('userProfile', data);
    } catch (e) {
      logger.error(`parse of GET request to ${path} failed`, {
        filename: 'userManager.ts',
        function: 'useUserProfile',
        error: e,
      });
      setShowError(true);
      throw e;
    }
  };
};

export const changePassword = (body: ChangePassword) => {
  const { ffSalesAppShouldUseUserManager } = getFlags();
  const put = ffSalesAppShouldUseUserManager
    ? secureHttpClient.putUserManager
    : secureHttpClient.putOperate;

  return put(
    {
      fetchingFunction: 'changePassword',
      fileName: 'changepassword',
    },
    'api/users/password',
    body,
    { isFormRequest: true },
  );
};
